'use client';
import React, { useEffect, useState } from 'react';

interface RoomLightingPlanProps {
  length: number;
  width: number;
  rows: number;
  cols: number;
  spacingX: number;
  spacingY: number;
  wallOffsetX: number;
  wallOffsetY: number;
  fixtureCount: number;
}

export default function RoomLightingPlan({
  length,
  width,
  rows,
  cols,
  wallOffsetX,
  wallOffsetY,
  spacingX,
  spacingY,
  fixtureCount,
}: RoomLightingPlanProps) {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    setVisibleCount(0);
    let count = 0;
    const interval = setInterval(() => {
      count += 1;
      setVisibleCount(count);
      if (count >= fixtureCount) clearInterval(interval);
    }, 80);
    return () => clearInterval(interval);
  }, [fixtureCount]);

  const CANVAS_W = 560;
  const CANVAS_H = Math.round(CANVAS_W * (width / length));
  const clampedH = Math.min(Math.max(CANVAS_H, 200), 480);

  const PADDING = 40;
  const drawW = CANVAS_W - PADDING * 2;
  const drawH = clampedH - PADDING * 2;

  const scaleX = drawW / length;
  const scaleY = drawH / width;

  const fixtures: { x: number; y: number; idx: number }[] = [];
  let idx = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (idx >= fixtureCount) break;
      const mX = wallOffsetX + c * spacingX;
      const mY = wallOffsetY + r * spacingY;
      const svgX = PADDING + mX * scaleX;
      const svgY = PADDING + mY * scaleY;
      fixtures.push({ x: svgX, y: svgY, idx });
      idx++;
    }
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${CANVAS_W} ${clampedH}`}
        className="w-full max-w-full rounded-xl border border-border bg-gray-50"
        style={{ minHeight: 200 }}
      >
        {/* Room outline */}
        <rect
          x={PADDING}
          y={PADDING}
          width={drawW}
          height={drawH}
          fill="white"
          stroke="#3F4044"
          strokeWidth="2"
          rx="4"
        />

        {/* Grid lines */}
        {Array.from({ length: cols - 1 }).map((_, i) => {
          const gx = PADDING + wallOffsetX * scaleX + (i + 0.5) * spacingX * scaleX;
          return (
            <line
              key={`grid-col-${i + 1}`}
              x1={gx}
              y1={PADDING}
              x2={gx}
              y2={PADDING + drawH}
              stroke="#E2DDD5"
              strokeWidth="1"
              strokeDasharray="4 4"
            />
          );
        })}
        {Array.from({ length: rows - 1 }).map((_, i) => {
          const gy = PADDING + wallOffsetY * scaleY + (i + 0.5) * spacingY * scaleY;
          return (
            <line
              key={`grid-row-${i + 1}`}
              x1={PADDING}
              y1={gy}
              x2={PADDING + drawW}
              y2={gy}
              stroke="#E2DDD5"
              strokeWidth="1"
              strokeDasharray="4 4"
            />
          );
        })}

        {/* Dimension labels */}
        <text x={CANVAS_W / 2} y={PADDING - 10} textAnchor="middle" fontSize="12" fill="#6B7280" fontFamily="Cairo, sans-serif">
          {length} م
        </text>
        <text x={12} y={clampedH / 2} textAnchor="middle" fontSize="12" fill="#6B7280" fontFamily="Cairo, sans-serif" transform={`rotate(-90, 12, ${clampedH / 2})`}>
          {width} م
        </text>

        {/* Fixtures */}
        {fixtures.map(({ x, y, idx: fIdx }) => (
          <g key={`fixture-${fIdx + 1}`} style={{ opacity: fIdx < visibleCount ? 1 : 0, transition: 'opacity 0.3s ease' }}>
            {/* Glow */}
            <circle cx={x} cy={y} r={18} fill="rgba(247,148,29,0.1)" />
            {/* Outer ring */}
            <circle cx={x} cy={y} r={10} fill="white" stroke="#F7941D" strokeWidth="2" />
            {/* Inner dot */}
            <circle cx={x} cy={y} r={4} fill="#F7941D" />
            {/* Number */}
            <text x={x} y={y + 24} textAnchor="middle" fontSize="9" fill="#6B7280" fontFamily="Cairo, sans-serif">
              {fIdx + 1}
            </text>
          </g>
        ))}

        {/* Corner markers */}
        {[
          [PADDING, PADDING],
          [PADDING + drawW, PADDING],
          [PADDING, PADDING + drawH],
          [PADDING + drawW, PADDING + drawH],
        ].map(([cx, cy], ci) => (
          <circle key={`corner-${ci + 1}`} cx={cx} cy={cy} r={3} fill="#3F4044" />
        ))}
      </svg>

      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full border-2 border-primary bg-white flex items-center justify-center">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
          </div>
          <span>مصباح</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 h-0.5 bg-border border-dashed" style={{ borderTop: '1px dashed #E2DDD5' }} />
          <span>خطوط التقسيم</span>
        </div>
      </div>
    </div>
  );
}